<template>
  <div class="mts-button-group" v-bind="$attrs">
    <span v-if="loading" class="text-[#8C8C8C]">
      <LoadingOutlined spin class="mr-1" />
      {{ t('common.apiSelectNotFound') }}
    </span>

    <span v-if="!loading && !getOptions.length" class="text-[#8C8C8C]">暂无数据</span>

    <MButton
      v-for="item in getOptions"
      :key="`${item.value}`"
      :disabled="item.disabled"
      :color="curValue.includes(item.value) ? 'green' : ''"
      :class="[
        {
          active: curValue.includes(item.value),
          'corner-tag': multiple,
          invisible: item.invisible,
        },
      ]"
      v-bind="buttonProps"
      @click="handleClick(item)"
    >
      <MIcon v-if="item.icon" class="align--1px mr-4px" :icon="item.icon" />
      <slot v-if="$slots.itemRender" name="itemRender" v-bind="{ item }"> </slot>
      <template v-else>{{ item.label }}</template>
    </MButton>
  </div>
</template>

<script lang="ts" setup>
  import { LoadingOutlined } from '@ant-design/icons-vue';
  import { get, isEqual } from 'lodash-es';
  import { computed, ComputedRef, PropType, ref, unref, watch } from 'vue';
  import type { Fn, Option, Recordable } from '@mts-fe/types';
  import { useRuleFormItem } from '@mts-fe/hooks';
  import { cloneDeep, isArray, isFunction, omit, propTypes } from '@mts-fe/utils';
  import { MButton, MButtonProps } from '@mts/components/Button';
  import { t } from '@mts/hooks/web/useI18n';

  defineOptions({ name: 'ApiButtonGroup', inheritAttrs: false });

  type RawValue = string | number;

  const props = defineProps({
    value: { type: [Array, String, Number] as PropType<RawValue | RawValue[] | undefined> },
    numberToString: propTypes.bool.def(true),
    buttonProps: { type: Object as PropType<MButtonProps> },
    /** 是否多选 */
    multiple: propTypes.bool,
    useFirstValue: propTypes.bool,
    cancelble: propTypes.bool,
    /** 手动模式 */
    manualMode: propTypes.bool,
    api: {
      type: Function as PropType<(arg?: any) => Promise<Option[] | Recordable<any>>>,
      default: null,
    },
    // api params
    params: propTypes.any.def({}),
    // support xxx.xxx.xx
    resultField: propTypes.string.def(''),
    labelField: propTypes.string.def('label'),
    valueField: propTypes.string.def('value'),
    keyField: propTypes.string.def('key'),
    immediate: propTypes.bool.def(true),
    options: {
      type: Array<Option>,
      default: [],
    },
    beforeFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
    afterFetch: {
      type: Function as PropType<Fn>,
      default: null,
    },
  });

  const emit = defineEmits(['options-change', 'change', 'update:value']);

  const optionsRef = ref<Option[]>([]);

  const loading = ref(false);
  const emitData = ref<Option[]>([]);

  // Embedded in the form, just use the hook binding to perform form verification
  const [state] = useRuleFormItem(props, 'value', 'change', emitData);

  const curValue = computed(() => {
    if (props.manualMode) return props.value ?? [];
    const val = state.value || props.value;
    return isArray(val) ? val : [val];
  }) as ComputedRef<RawValue[]>;

  const getOptions = computed(() => {
    const { labelField, valueField, keyField, numberToString } = props;

    const data = unref(optionsRef).reduce((prev, next: any) => {
      if (next) {
        const value = get(next, valueField);
        prev.push({
          ...omit(next, [labelField, valueField]),
          key: get(next, keyField) || value,
          label: get(next, labelField),
          value: numberToString ? `${value}` : value,
        });
      }
      return prev;
    }, [] as Option[]);
    return data.length > 0 ? data : props.options;
  });

  watch(
    () => state.value,
    (v) => {
      emit('update:value', v);
    },
  );

  watch(
    () => props.params,
    (value, oldValue) => {
      if (isEqual(value, oldValue)) return;
      loading.value = false;
      fetch();
    },
    { deep: true, immediate: props.immediate },
  );

  async function fetch() {
    let { api, beforeFetch, afterFetch, params, resultField } = props;
    if (!api || !isFunction(api) || loading.value) return;
    optionsRef.value = [];
    try {
      loading.value = true;
      if (beforeFetch && isFunction(beforeFetch)) {
        const res = await beforeFetch(params);
        if (typeof res === 'boolean' && !res) return;
        params = res || params;
      }
      let res = await api(params);
      if (afterFetch && isFunction(afterFetch)) {
        res = (await afterFetch(res)) || res;
      }
      if (isArray(res)) {
        optionsRef.value = [...res];
        emitChange();
        return;
      }
      if (resultField) {
        optionsRef.value = get(res, resultField) || [];
      }
      emitChange();
    } catch (error) {
      console.warn(error);
    } finally {
      loading.value = false;
    }
  }

  function emitChange() {
    emit('options-change', unref(getOptions));

    props.useFirstValue && !state.value && handleClick(unref(getOptions)[0]);
  }

  function handleClick(item: Option) {
    // console.log('handleClick', item);
    if (props.manualMode) return emit('change', item.value, item);

    if (props.multiple) {
      const valueList = cloneDeep(state.value || []) as RawValue[];
      const curVal = item.value;
      const valueIndex = valueList.indexOf(curVal);
      if (valueIndex > -1) {
        valueList.splice(valueIndex, 1);
      } else {
        valueList.push(curVal);
      }
      state.value = valueList;
    } else {
      state.value = props.cancelble && state.value === item.value ? void 0 : item.value;
    }
    emitData.value = [item];
  }
</script>

<style lang="less">
  .mts-button-group {
    display: flex;
    flex-flow: row wrap;
    gap: 8px;
    line-height: @control-height;

    .mts-btn-green {
      background-color: #edfdf9;
    }

    .corner-tag.active:not(.is-disabled) {
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        right: 0;
        width: 18px;
        height: 12px;
        border-radius: 0 0 0 6px;
        background-color: #01a887;
      }

      &::after {
        content: '';
        position: absolute;
        top: 1px;
        right: 4px;
        width: 9px;
        height: 5px;
        transform: rotate(135deg);
        border: 1.5px solid #fff;
        border-bottom-color: transparent;
        border-left-color: transparent;
      }
    }
  }
</style>
